import { useSyncExternalStore, type ReactNode } from "react";

import { cn } from "../cn";
import {
  getAppearance,
  setAppearance,
  subscribeToAppearance,
  type Appearance,
} from "./appearance";

const choices: Array<{ id: Appearance; label: string; compactLabel: string; icon: ReactNode }> =
  [
    { id: "light", label: "Light appearance", compactLabel: "Light", icon: <SunIcon /> },
    {
      id: "system",
      label: "Follow system appearance",
      compactLabel: "System",
      icon: <SystemIcon />,
    },
    { id: "dark", label: "Dark appearance", compactLabel: "Dark", icon: <MoonIcon /> },
  ];

/** Direct icon controls keep all three appearance choices one press away. */
export function AppearanceSwitcher({ className }: { className?: string }) {
  const appearance = useSyncExternalStore(subscribeToAppearance, getAppearance, () => "system");
  return (
    <div
      role="radiogroup"
      aria-label="Appearance"
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-sm border border-rule bg-surface/60 p-0.5",
        className,
      )}
    >
      {choices.map((choice) => (
        <button
          key={choice.id}
          type="button"
          aria-label={choice.label}
          role="radio"
          aria-checked={choice.id === appearance}
          tabIndex={choice.id === appearance ? 0 : -1}
          onKeyDown={(event) => {
            const index = choices.indexOf(choice);
            const next =
              event.key === "ArrowRight"
                ? (index + 1) % choices.length
                : event.key === "ArrowLeft"
                  ? (index + choices.length - 1) % choices.length
                  : event.key === "Home"
                    ? 0
                    : event.key === "End"
                      ? choices.length - 1
                      : undefined;
            if (next === undefined) return;
            event.preventDefault();
            setAppearance(choices[next]!.id);
            event.currentTarget.parentElement
              ?.querySelectorAll<HTMLButtonElement>("button")
              .item(next)
              ?.focus();
          }}
          title={choice.label}
          onClick={() => setAppearance(choice.id)}
          className={cn(
            "inline-flex size-7 items-center justify-center rounded-[4px] transition-[color,background-color,transform] duration-200 focus-visible:outline-2 focus-visible:outline-accent motion-safe:active:scale-90",
            choice.id === appearance
              ? "bg-surface-2 text-ink"
              : "text-ink-3 hover:bg-surface-2/60 hover:text-ink",
          )}
        >
          {choice.icon}
        </button>
      ))}
    </div>
  );
}

function SunIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="size-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    >
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />
    </svg>
  );
}
